<template>
  <div class="d-flex flex-column mb-6">
    <span class="font-weight-bold font-size-24">Дашборд</span>
    <span class="font-size-13 text-medium-emphasis">
      Огляд документів за статусами обробки
    </span>
  </div>

  <b-section-loader :loading="$loading.isLoading('initHomeDashboard')">
    <div v-if="isInitialized" class="d-flex flex-column ga-7">
      <v-row>
        <v-col v-for="card in queueCards" :key="card.key" cols="12" lg="3">
          <v-hover>
            <template #default="{ isHovering, props }">
              <v-card
                v-bind="props"
                border
                class="h-100 dashboard-queue-card cursor-pointer"
                :class="[
                  getQueueCardTextClass(card.key),
                  { 'dashboard-queue-card--hover': isHovering },
                ]">
                <v-card-text>
                  <div class="d-flex align-center ga-4 mb-3">
                    <v-avatar
                      class="rounded-lg"
                      :class="getQueueCardBgClass(card.key)"
                      size="46">
                      <v-icon :color="getQueueCardColor(card.key)" size="23">
                        {{ getQueueCardIcon(card.key) }}
                      </v-icon>
                    </v-avatar>

                    <div>
                      <div class="font-size-15 font-weight-bold">
                        {{ card.title }}
                      </div>
                      <div
                        class="font-size-32 font-weight-bold line-height-1"
                        :class="`text-${getQueueCardColor(card.key)}`">
                        {{ card.value }}
                      </div>
                    </div>
                  </div>

                  <v-divider class="mb-4"></v-divider>

                  <div class="d-flex flex-column">
                    <div
                      v-for="item in card.items"
                      :key="item.title"
                      class="d-flex align-center justify-space-between ga-3 cursor-pointer dashboard-queue-item-title"
                      :class="{ 'text-disabled': item.disabled }"
                      @click="openQueueItem(item)">
                      <span class="font-size-13 text-medium-emphasis">
                        {{ item.title }}
                      </span>

                      <div class="d-flex align-center ga-2">
                        <span
                          class="font-size-14 font-weight-bold"
                          :class="
                            item.disabled
                              ? 'text-disabled'
                              : `text-${getQueueCardColor(card.key)}`
                          ">
                          {{ item.value }}
                        </span>
                        <v-icon color="grey-lighten-1" size="small">
                          mdi-chevron-right
                        </v-icon>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </template>
          </v-hover>
        </v-col>
      </v-row>

      <div>
        <div class="d-flex align-center justify-space-between ga-3 mb-4">
          <div class="font-size-15 font-weight-bold">Показники обробки</div>

          <v-btn-toggle
            v-model="selectedPeriod"
            class="dashboard-period-toggle bg-grey-lighten-3 pa-1"
            density="comfortable"
            mandatory
            rounded="lg"
            variant="flat">
            <v-btn
              rounded="lg"
              :class="{
                'bg-white text-blue-darken-3': selectedPeriod === 'week',
                'text-medium-emphasis': selectedPeriod !== 'week',
              }"
              value="week">
              <span class="font-size-13 font-weight-bold text-none">
                За тиждень
              </span>
            </v-btn>
            <v-btn
              rounded="lg"
              :class="{
                'bg-white text-blue-darken-3': selectedPeriod === 'month',
                'text-medium-emphasis': selectedPeriod !== 'month',
              }"
              value="month">
              <span class="font-size-13 font-weight-bold text-none">
                За місяць
              </span>
            </v-btn>
          </v-btn-toggle>
        </div>

        <v-row>
          <v-col
            v-for="stat in processingStats"
            :key="stat.key"
            cols="12"
            lg="3"
            sm="6">
            <v-card border class="h-100">
              <v-card-text class="d-flex flex-column h-100">
                <div class="d-flex align-center ga-4 mb-3">
                  <v-avatar class="rounded-lg" :class="stat.bgClass" size="40">
                    <v-icon :color="stat.color" size="20">
                      {{ stat.icon }}
                    </v-icon>
                  </v-avatar>

                  <div
                    class="font-size-14 font-weight-bold text-medium-emphasis">
                    {{ stat.title }}
                  </div>
                </div>

                <div class="mt-auto">
                  <div class="d-flex align-end ga-2">
                    <span
                      class="font-size-30 font-weight-bold line-height-1"
                      style="line-height: initial">
                      {{ stat.value }}
                    </span>

                    <span
                      class="d-flex align-end ga-1 font-size-13 font-weight-bold line-height-1"
                      :class="{
                        'text-success': stat.diff > 0,
                        'text-error': stat.diff < 0,
                        'text-disabled': stat.diff === 0,
                      }">
                      <v-icon size="18">
                        {{
                          stat.diff > 0
                            ? 'mdi-chevron-up'
                            : stat.diff < 0
                            ? 'mdi-chevron-down'
                            : 'mdi-minus'
                        }}
                      </v-icon>
                      <span>{{ Math.abs(stat.diff) }}</span>
                    </span>
                  </div>

                  <div class="font-size-12 text-disabled mt-2">
                    проти попереднього періоду
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div>
        <div class="font-size-15 font-weight-bold mb-4">
          Документи за департаментами ·
          {{ selectedPeriod === 'week' ? 'за тиждень' : 'за місяць' }}
        </div>

        <v-card border>
          <v-data-table
            :headers="departmentHeaders"
            :items="departmentRows"
            :items-per-page="-1">
            <template #headers="{ columns }">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key || undefined"
                  class="font-size-14 text-disabled text-uppercase"
                  :class="{ 'text-center': column.align === 'center' }">
                  {{ column.title }}
                </th>
              </tr>
            </template>

            <template #item.name="{ item }">
              <span class="font-size-15 font-weight-bold">
                {{ item.name }}
              </span>
            </template>

            <template #item.reviewed="{ item }">
              <span class="font-size-14 font-weight-bold text-blue">
                {{ item.reviewed }}
              </span>
            </template>

            <template #item.agreed="{ item }">
              <span
                class="font-size-14 font-weight-bold text-deep-purple-accent-3">
                {{ item.agreed }}
              </span>
            </template>

            <template #item.approved="{ item }">
              <span class="font-size-14 font-weight-bold text-warning">
                {{ item.approved }}
              </span>
            </template>

            <template #item.signed="{ item }">
              <span class="font-size-14 font-weight-bold text-success">
                {{ item.signed }}
              </span>
            </template>

            <template #item.total="{ item }">
              <div class="d-flex align-center ga-4">
                <v-progress-linear
                  bg-color="grey-lighten-3"
                  color="deep-purple-accent-3"
                  height="8"
                  :model-value="getProgressValue(item.total)"
                  rounded></v-progress-linear>

                <span class="font-size-14 font-weight-bold text-no-wrap">
                  {{ item.total }}
                </span>
              </div>
            </template>

            <template #bottom></template>
          </v-data-table>
        </v-card>
      </div>
    </div>
  </b-section-loader>
</template>

<script setup lang="ts">
import { BSectionLoader } from 'best-modules/components'
import { initHomeDashboard } from './dashboard'

defineOptions({
  name: 'HomePage',
})

const {
  isInitialized,
  selectedPeriod,
  queueCards,
  processingStats,
  departmentRows,
  departmentHeaders,
  getProgressValue,
  getQueueCardIcon,
  getQueueCardColor,
  getQueueCardBgClass,
  getQueueCardTextClass,
  openQueueItem,
} = initHomeDashboard()
</script>

<style lang="scss" scoped>
.dashboard-queue-card {
  max-height: 215px;
  transition: border-color 0.2s ease;
}

.dashboard-queue-card--hover {
  border-color: currentColor !important;
}

.dashboard-queue-card > .v-card-text {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.dashboard-queue-item-title {
  border-radius: 6px;
  padding: 4px 8px;
  transition: background-color 0.2s ease;
}

.dashboard-queue-item-title:hover {
  background-color: #f7f8fb;
}

.dashboard-period-toggle {
  background-color: #eeeeee !important;
  box-shadow: none;
}

.dashboard-period-toggle :deep(.v-btn) {
  min-width: 120px;
  background-color: transparent;
}

.dashboard-period-toggle :deep(.v-btn__overlay) {
  display: none;
}
</style>
